import { registerAuditLabels } from "@vixio/ui";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo signatures (ver modules/signatures/README.md). */

export type EnvelopeStatus = "draft" | "sent" | "completed" | "canceled" | "expired";
export type SignerStatus = "pending" | "viewed" | "signed" | "declined";
export type AuthMethod = "email_otp" | "whatsapp_otp";

export const ENVELOPE_STATUS: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  sent: { label: "aguardando assinaturas", tone: "info" },
  completed: { label: "concluído", tone: "ok" },
  canceled: { label: "cancelado", tone: "neutral" },
  expired: { label: "expirado", tone: "warn" },
};

export const SIGNER_STATUS: StatusMap = {
  pending: { label: "pendente", tone: "neutral" },
  viewed: { label: "abriu o link", tone: "info" },
  signed: { label: "assinou", tone: "ok" },
  declined: { label: "recusou", tone: "crit" },
};

export const METHOD_LABEL: Record<AuthMethod, string> = { email_otp: "Código por e-mail", whatsapp_otp: "Código por WhatsApp" };

export const ROLE_OPTIONS = [
  { value: "contratante", label: "Contratante" },
  { value: "contratada", label: "Contratada" },
  { value: "testemunha", label: "Testemunha" },
  { value: "responsavel_legal", label: "Responsável legal" },
  { value: "signatario", label: "Signatário" },
];
export const roleLabel = (r: string) => ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r.replace(/_/g, " ");

export const EVENT_LABEL: Record<string, string> = {
  created: "Envelope criado",
  signer_added: "Signatário incluído",
  signer_removed: "Signatário removido",
  sent: "Enviado para assinatura",
  viewed: "Link aberto",
  otp_sent: "Código de verificação enviado",
  otp_verified: "Código confirmado",
  otp_failed: "Código recusado",
  signed: "Assinou",
  declined: "Recusou",
  completed: "Concluído: manifesto gerado",
  canceled: "Cancelado",
  expired: "Expirado",
  sealed: "PDF final selado",
  resent: "Link reenviado",
};

export interface EnvelopeView {
  id: string;
  org_id: string;
  title: string;
  document_id: string;
  document_code: string;
  document_sha256: string;
  status: EnvelopeStatus;
  signing_order: "sequential" | "parallel";
  expires_at: string;
  sent_at: string | null;
  completed_at: string | null;
  canceled_at: string | null;
  cancel_reason: string | null;
  expired_at: string | null;
  manifest_sha256: string | null;
  final_sha256: string | null;
  final_storage_path: string | null;
  sealed_at: string | null;
  signers_total: number;
  signers_signed: number;
  next_signer: string | null;
  created_at: string;
  updated_at: string;
}

export interface Signer {
  id: string;
  envelope_id: string;
  position: number;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  auth_method: AuthMethod;
  token_issued_at: string | null;
  status: SignerStatus;
  viewed_at: string | null;
  otp_verified_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  ip_hash: string | null;
  user_agent: string | null;
}

export interface SigEvent {
  id: string;
  seq: number;
  envelope_id: string;
  signer_id: string | null;
  kind: string;
  at: string;
  ip_hash: string | null;
  user_agent: string | null;
  data: Record<string, unknown>;
}

export interface SendResultItem {
  signer_id: string;
  position: number;
  name: string;
  email: string | null;
  phone: string | null;
  auth_method: AuthMethod;
  signing_url_token: string;
}

export interface PublicEnvelope {
  envelope: { title: string; message: string | null; status: EnvelopeStatus; signing_order: string; expires_at: string; completed_at: string | null };
  document: { code: string; type: string; type_label: string; title: string; status: string; sha256: string; public: Record<string, unknown> };
  signer: {
    name: string;
    role: string;
    position: number;
    status: SignerStatus;
    email_masked: string | null;
    phone_masked: string | null;
    auth_method: AuthMethod;
    requires_otp: boolean;
    otp_verified: boolean;
    can_sign: boolean;
    waiting_for: string[];
  };
  signers: Array<{ name: string; role: string; position: number; status: SignerStatus; signed_at: string | null }>;
  accept_text: string;
}

export type EnvelopeCheck =
  | { found: false }
  | {
      found: true;
      status: EnvelopeStatus;
      title: string;
      document_code: string;
      document_sha256_prefix: string;
      signing_order: string;
      sent_at: string | null;
      completed_at: string | null;
      completed_at_local: string | null;
      manifest_sha256: string | null;
      final_sha256_prefix: string | null;
      sealed_at: string | null;
      signers: Array<{ name: string; role: string; status: SignerStatus; signed_at: string | null }>;
    };

export function signingUrl(token: string): string {
  return `${window.location.origin}/assinar/${token}`;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "signatures.create_envelope": {
      args: { p_org: string; p_title: string; p_document_id: string; p_signing_order?: "sequential" | "parallel"; p_expires_at?: string | null; p_message?: string | null };
      returns: string;
    };
    "signatures.add_signer": {
      args: { p_envelope: string; p_name: string; p_email?: string | null; p_phone?: string | null; p_role?: string; p_auth_method?: AuthMethod; p_position?: number | null };
      returns: string;
    };
    "signatures.remove_signer": { args: { p_signer: string }; returns: null };
    "signatures.send_envelope": { args: { p_envelope: string }; returns: SendResultItem[] };
    "signatures.resend": { args: { p_signer: string }; returns: SendResultItem | { signer_id: string; signing_url_token: string } };
    "signatures.cancel_envelope": { args: { p_envelope: string; p_reason: string }; returns: null };
    "signatures.seal": { args: { p_envelope: string; p_final_sha256: string; p_storage_path?: string | null }; returns: null };
    "signatures.open": { args: { p_token: string }; returns: PublicEnvelope };
    "signatures.request_otp": { args: { p_token: string }; returns: { channel: "email" | "whatsapp"; destination_masked: string; expires_at: string } };
    "signatures.verify_otp": { args: { p_token: string; p_code: string }; returns: { verified: boolean; reason?: "invalid" | "expired" | "locked"; attempts_left?: number } };
    "signatures.sign": { args: { p_token: string; p_signature_image: string | null; p_accept_text: boolean }; returns: Record<string, unknown> };
    "signatures.decline": { args: { p_token: string; p_reason: string }; returns: Record<string, unknown> };
    "signatures.verify_envelope": { args: { p_code_or_document_id: string }; returns: EnvelopeCheck };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "envelope.created": "Envelope criado",
  "envelope.signer_added": "Signatário incluído",
  "envelope.signer_removed": "Signatário removido",
  "envelope.sent": "Envelope enviado",
  "envelope.link_rotated": "Link reenviado",
  "envelope.canceled": "Envelope cancelado",
  "envelope.completed": "Envelope concluído",
  "envelope.sealed": "PDF final selado",
  "envelope.public_verified": "Assinaturas verificadas publicamente",
  "envelope.public_verify_miss": "Verificação sem envelope",
  "signature.viewed": "Link aberto pelo signatário",
  "signature.otp_requested": "Código pedido",
  "signature.otp_verified": "Código confirmado",
  "signature.signed": "Assinado",
  "signature.declined": "Recusado pelo signatário",
});
